/**
 * PDF export — captures the current page-frame as an SVG snapshot
 * (via html-to-image), wraps it in a paper-sized HTML document, and
 * POSTs to the backend Gotenberg endpoint which returns a PDF for
 * download.
 *
 * Why html-to-image and not a hand-rolled SVG renderer:
 *   - Captures the live DOM exactly as drawn (labels, badges,
 *     EXISTING boundaries, custom symbol content, title block) with
 *     zero re-implementation cost.
 *   - The captured SVG embeds the foreignObject of HTML labels, which
 *     Chromium (Gotenberg's renderer) handles cleanly.
 *   - Future-proof: when we add new symbol types, export just works.
 *
 * Why Gotenberg instead of a browser-side jsPDF:
 *   - User asked for it and the SP project already has Gotenberg
 *     wired up (publish-link-v2.php pattern).
 *   - Vector quality: Chromium prints SVG to PDF as vectors, no
 *     pixel rasterisation.
 *   - Consistent fonts (server-side font availability).
 *
 * The export viewport reset:
 *   React Flow renders nodes inside a CSS-transformed pane. To
 *   capture the page-frame at native paper size (regardless of zoom
 *   the user happens to be at), we temporarily set the viewport to
 *   { x: 0, y: 0, zoom: 1 } so the page-frame at world {0,0} sits at
 *   screen {0,0}, then capture an exact W×H rectangle. Restored
 *   immediately afterward.
 */
import { toSvg } from 'html-to-image';
import { getNodesBounds } from '@xyflow/react';
import type { Topology } from '../types/topology';
import { PAPER_DIMENSIONS, type PaperSize, type SldNode } from '../store/editor';
import { sldApiUrl } from '../api/sldApiUrl';
import { inlineImagesForCapture } from './inlineImages';

/** What a successful export reports back to the caller. Non-fatal — the
 *  PDF downloaded fine — but a non-empty `missingImages` means the sheet
 *  went out with a blank box where a logo should be, which the caller
 *  beacons to the failure log so we hear about it without waiting for a
 *  client to notice. */
export interface ExportPdfResult {
  /** Absolute URLs of images that could not be embedded in the capture. */
  missingImages: string[];
}

interface ExportOptions {
  topology: Topology;
  paperSize: PaperSize;
  /** React Flow's setViewport — capture wants viewport at identity. */
  setViewport: (vp: { x: number; y: number; zoom: number }) => void;
  /** React Flow's getViewport — to save/restore. */
  getViewport: () => { x: number; y: number; zoom: number };
  /**
   * When true, the "Generated with Solar Proof" footer is suppressed.
   * Standalone (free-tier) exports always carry the credit line; once
   * SP-side accounts are wired in, callers should pass true for paid
   * Pro accounts so their PDFs go out un-stamped.
   */
  isPro?: boolean;
  /**
   * Current canvas nodes — used for the fit-to-page safety net. When a
   * drawing is larger than the paper (big commercial multi-inverter
   * jobs), we scale it DOWN to fit so nothing clips off the page edge.
   * Optional: when omitted, capture falls back to the legacy identity
   * viewport (exact page-sized crop from the origin).
   */
  nodes?: SldNode[];
}

/** Page inset (px) kept clear around the drawing when it must be
 *  scaled to fit. Small — the title block already has its own margin. */
const FIT_MARGIN = 16;

const PX_PER_INCH = 96;

/** 1×1 FULLY TRANSPARENT PNG — RGBA(0,0,0,0). Passed to html-to-image
 *  as imagePlaceholder so an image that fails to FETCH during capture
 *  (a brand/installer logo 404ing on the server, a network blip)
 *  degrades to an invisible pixel instead of rejecting the whole
 *  capture. A missing logo on the PDF beats no PDF at all. (Prod
 *  incident 2026-08: a client's export failed with "[object Event]" —
 *  an image element's raw error Event thrown from the capture's
 *  img.onerror path.)
 *
 *  DO NOT swap this for a hand-copied "transparent pixel" data URL
 *  without decoding it first. The value that shipped with the original
 *  fix was captioned "transparent" but actually decoded to
 *  RGBA(0, 0, 255, 127) — a half-opaque BLUE pixel. Stretched across a
 *  50×30 logo box by preserveAspectRatio it painted a solid
 *  purple-blue rectangle on every PDF whose logo failed to embed, which
 *  is exactly how this bug was reported ("the logo rendered a purple
 *  square"). The bytes below are 0x89 PNG / IHDR 1×1 RGBA / one
 *  zero-filled scanline — verified, not copied. */
const IMAGE_PLACEHOLDER =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR4nGNgAAIAAAUAAXpeqz8AAAAASUVORK5CYII=';

/**
 * html-to-image rejects with whatever the underlying failure handed it
 * — for image-load failures that's the DOM error EVENT, not an Error.
 * Uncaught, that surfaces to the user as the useless "[object Event]".
 * Normalise anything non-Error into an Error whose message names the
 * failing element/URL as best we can, so the dialog, console and the
 * server-side failure log all get something actionable.
 */
function toCaptureError(e: unknown): Error {
  if (e instanceof Error) return e;
  if (typeof Event !== 'undefined' && e instanceof Event) {
    const t = e.target as
      | (Partial<HTMLImageElement> & { tagName?: string; href?: { baseVal?: string } })
      | null;
    let src = t?.currentSrc || t?.src || t?.href?.baseVal || '';
    // Embedded images are data: URLs by the time they decode — the
    // full URL is huge and meaningless; say what happened instead.
    if (src.startsWith('data:')) {
      src = '(an embedded image failed to decode — likely a logo URL returning a non-image, e.g. a 404 page)';
    } else if (src.length > 160) {
      src = src.slice(0, 160) + '…';
    }
    return new Error(
      `Diagram capture failed while loading an image` +
        (src ? `: ${src}` : '') +
        (t?.tagName ? ` [<${String(t.tagName).toLowerCase()}>]` : '')
    );
  }
  try {
    return new Error(typeof e === 'string' ? e : JSON.stringify(e));
  } catch {
    return new Error(String(e));
  }
}

/** Promise that resolves on the next paint frame — gives React Flow
 *  a chance to apply the viewport reset before we read the DOM. */
function nextFrame(): Promise<void> {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  );
}

export async function exportPdf({
  topology,
  paperSize,
  setViewport,
  getViewport,
  isPro,
  nodes,
}: ExportOptions): Promise<ExportPdfResult> {
  const dims = PAPER_DIMENSIONS[paperSize];

  // Save viewport state so we can put it back exactly where the user
  // had it — they shouldn't notice the export happened beyond the
  // brief loading state.
  const savedViewport = getViewport();

  // ----- Fit-to-page capture viewport -----
  // Default is the legacy identity viewport: page-frame at world {0,0}
  // sits at screen {0,0} and we crop an exact paper-sized rectangle.
  // That clips anything drawn beyond the paper. So when the drawing's
  // bounding box overflows the page (tall commercial multi-inverter
  // jobs), compute a scale that fits the whole drawing inside the
  // paper and anchor it at the top-left margin. We only ever scale
  // DOWN (cap at zoom 1): a normal page-sized drawing keeps the exact
  // identity capture it has today, so existing exports are unchanged.
  let captureVp = { x: 0, y: 0, zoom: 1 };
  const contentNodes = (nodes ?? []).filter((n) => n.id !== '__page_frame__');
  if (contentNodes.length > 0) {
    const b = getNodesBounds(contentNodes);
    const overflows =
      b.x < 0 || b.y < 0 ||
      b.x + b.width > dims.w || b.y + b.height > dims.h;
    if (overflows && b.width > 0 && b.height > 0) {
      const scale = Math.min(
        (dims.w - 2 * FIT_MARGIN) / b.width,
        (dims.h - 2 * FIT_MARGIN) / b.height,
        1,
      );
      captureVp = {
        x: FIT_MARGIN - b.x * scale,
        y: FIT_MARGIN - b.y * scale,
        zoom: scale > 0 ? scale : 1,
      };
    }
  }
  setViewport(captureVp);

  // Two animation frames: one for React Flow to commit the viewport
  // change to its CSS transform, one to let layout settle.
  await nextFrame();

  const viewportEl = document.querySelector(
    '.react-flow__viewport'
  ) as HTMLElement | null;

  if (!viewportEl) {
    setViewport(savedViewport);
    throw new Error('Could not find React Flow viewport element');
  }

  // Editor-only chrome that shouldn't appear in the printed PDF:
  //   - The page-frame node (dashed grey border + "A4 Landscape" label)
  //   - The React Flow background dot grid
  //   - Selection outlines on currently-selected nodes
  // Hide them by appending a transient <style> element rather than
  // mutating each node's inline style — fewer moving parts, easier to
  // reverse, and html-to-image picks up the CSS in computed styles.
  const printHideStyle = document.createElement('style');
  printHideStyle.setAttribute('data-pdf-export', 'true');
  printHideStyle.textContent = `
    /* Hide the page-frame chrome (dashed border + paper-size label).
       React Flow tags each node wrapper with data-id, so we can target
       the page-frame precisely without touching its sibling nodes. */
    .react-flow__node[data-id="__page_frame__"] { display: none !important; }
    /* Background dot grid — purely an editor aid. */
    .react-flow__background { display: none !important; }
    /* Selection outline on the currently-selected node — a UI signal
       that doesn't belong on the print. */
    .react-flow__node.selected > div,
    .react-flow__node:focus-visible > div { outline: none !important; }
    /* The interactive × delete button on a selected wire. */
    .react-flow__edge.selected button { display: none !important; }
  `;
  document.head.appendChild(printHideStyle);

  // Turn every external image reference (title-block installer logo,
  // brand logos in the BOM rows) into a data: URL BEFORE capture.
  // html-to-image would otherwise re-fetch each URL itself, and any
  // failure there is swallowed into imagePlaceholder — a logo that
  // renders perfectly in the editor silently becoming a blank box on
  // the PDF. See inlineImages.ts for the full reasoning; the short
  // version is that its refetch is a different request from the one
  // that painted the logo (cacheBust-forced miss, CORS rules, no
  // content sniffing) and fails in cases the original never did.
  //
  // Never fatal: an image we can't resolve is left alone and reported.
  let missingImages: string[] = [];
  let restoreImages: () => void = () => {};
  try {
    const inlined = await inlineImagesForCapture(viewportEl);
    restoreImages = inlined.restore;
    missingImages = inlined.failed;
    if (missingImages.length > 0) {
      console.warn(
        `SLD export: ${missingImages.length} image(s) could not be embedded and ` +
          `will be blank on the PDF:`,
        missingImages,
      );
    }
  } catch (e) {
    // Pre-inlining is an optimisation over html-to-image's own path,
    // not a prerequisite. If it blows up entirely, carry on and let
    // html-to-image try — worst case we're back to the old behaviour.
    console.warn('SLD export: image pre-inlining failed, continuing', e);
  }

  // Capture as SVG. We pass explicit width/height so the output is
  // exactly paper-sized regardless of the captured element's bounding
  // rect. backgroundColor white so transparent regions render white
  // (matches A4 paper expectations).
  let svgDataUrl: string;
  try {
    svgDataUrl = await toSvg(viewportEl, {
      width: dims.w,
      height: dims.h,
      backgroundColor: '#ffffff',
      cacheBust: true,
      // Failed image FETCHES (404 logo, network blip) become an
      // invisible pixel instead of aborting the capture — see
      // IMAGE_PLACEHOLDER above.
      imagePlaceholder: IMAGE_PLACEHOLDER,
      // Second net: imagePlaceholder only covers a failed FETCH. If the
      // bytes come back but the clone can't DECODE them, html-to-image
      // rejects with the raw img.onerror Event — the original
      // "[object Event]" failure. Handling it here resolves instead,
      // leaving that one image empty rather than killing the export.
      onImageErrorHandler: (...args: unknown[]) => {
        console.warn('SLD export: an image failed to decode during capture', ...args);
      },
      filter: (node) => {
        if (!(node instanceof HTMLElement)) return true;
        // React Flow chrome that the viewport may pick up via portals.
        if (node.classList?.contains('react-flow__attribution')) return false;
        if (node.classList?.contains('react-flow__controls')) return false;
        if (node.classList?.contains('react-flow__minimap')) return false;
        // Belt-and-braces: also drop the page-frame node entirely.
        if (node.dataset?.id === '__page_frame__') return false;
        return true;
      },
    });
  } catch (e) {
    // Re-throw as a real Error naming the failing image where
    // possible — the raw DOM error Event stringifies to the useless
    // "[object Event]" a client actually screenshotted from prod.
    throw toCaptureError(e);
  } finally {
    // Restore the user's viewport, put the real image hrefs back and
    // remove our temporary stylesheet — even if capture failed.
    restoreImages();
    setViewport(savedViewport);
    printHideStyle.remove();
  }

  // toSvg returns a data: URL. Decode it back to raw SVG markup so we
  // can embed it inline in the HTML wrapper Gotenberg consumes.
  const svgMarkup = decodeURIComponent(
    svgDataUrl.replace(/^data:image\/svg\+xml;charset=utf-8,/, '')
  );

  // Wrap the SVG in a minimal HTML document sized exactly to the
  // paper. Setting width/height on body in pixels at 96dpi matches
  // Gotenberg's paperWidth/Height (in inches) when we divide by 96.
  const html = buildPdfHtml(svgMarkup, dims.w, dims.h, isPro === true);

  // POST to the backend. paperWidth/Height are in inches.
  const fd = new FormData();
  // The raw HTML document (inline <svg>, <style>, sometimes <script>-
  // looking strings inside data: URLs) trips the prod web application
  // firewall, which 403s the POST before PHP ever runs - the client
  // sees "Export failed (403)" and the server log stays empty. Ship
  // the document opaquely instead: gzip + base64 where the browser
  // supports CompressionStream, plain base64 otherwise. pdf.php
  // decodes `payload` per `payload_enc` and still accepts a legacy
  // `html` field from older builds.
  const { payload, enc } = await encodePayload(html);
  fd.append('payload', payload);
  fd.append('payload_enc', enc);
  fd.append('paperWidth', String(dims.w / PX_PER_INCH));
  fd.append('paperHeight', String(dims.h / PX_PER_INCH));
  fd.append('filename', deriveFilename(topology));
  // When the editor was opened for a Solar Proof project (?project=<qid>),
  // tell pdf.php which project this is so the export is filed as the
  // project's latest SLD document (and picked up by the user manual).
  try {
    const qid = new URLSearchParams(window.location.search).get('project');
    if (qid && /^\d+$/.test(qid)) fd.append('qid', qid);
  } catch {
    /* noop - filing is a nicety, the download still works */
  }

  const endpoint = sldApiUrl('pdf.php');
  const res = await fetch(endpoint, {
    method: 'POST',
    body: fd,
  });

  if (!res.ok) {
    // Try to surface a useful error message — backend returns JSON
    // for 4xx/5xx, PDF blob for 200.
    let detail = '';
    try {
      const j = await res.json();
      detail = j.error ? `${j.error}${j.detail ? ` — ${j.detail}` : ''}` : '';
    } catch {
      /* noop */
    }
    // Include the resolved URL so 404s are immediately diagnosable
    // (e.g. "is the endpoint at the path I think it is?").
    throw new Error(
      `Export failed (${res.status}) at ${endpoint}${detail ? ': ' + detail : ''}`
    );
  }

  const blob = await res.blob();
  triggerDownload(blob, deriveFilename(topology));

  return { missingImages };
}

/**
 * Encode the HTML document for transport past the WAF (see the call
 * site). Returns base64 text plus the encoding tag pdf.php switches on.
 */
async function encodePayload(html: string): Promise<{ payload: string; enc: 'gzip+b64' | 'b64' }> {
  const bytes = new TextEncoder().encode(html);
  let out: Uint8Array = bytes;
  let enc: 'gzip+b64' | 'b64' = 'b64';
  try {
    if (typeof CompressionStream !== 'undefined') {
      const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('gzip'));
      out = new Uint8Array(await new Response(stream).arrayBuffer());
      enc = 'gzip+b64';
    }
  } catch {
    out = bytes;
    enc = 'b64';
  }
  // Chunked btoa - String.fromCharCode(...bigArray) overflows the
  // argument limit on multi-MB payloads.
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < out.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, Array.from(out.subarray(i, i + CHUNK)));
  }
  return { payload: btoa(bin), enc };
}

/** Build the minimal HTML wrapper Gotenberg will rasterise. */
function buildPdfHtml(svgMarkup: string, w: number, h: number, isPro: boolean): string {
  // Free-tier credit footer. Tucked into the bottom-right corner so
  // it's outside the title block area (which sits along the bottom
  // edge spanning the full width) — actually, the title block already
  // covers the very bottom strip, so we sit the credit JUST ABOVE the
  // title block at right-aligned position. Discreet grey, small font.
  // Suppressed for paid Pro accounts via the isPro flag plumbed
  // through ExportOptions.
  const footer = isPro
    ? ''
    : `<div class="sp-credit">Generated with Solar Proof — solarproof.com.au</div>`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { width: ${w}px; height: ${h}px; background: #ffffff; }
  .frame { width: ${w}px; height: ${h}px; position: relative; overflow: hidden; }
  .frame > svg { width: 100%; height: 100%; display: block; }
  /* The captured SVG sometimes carries a translucent grid pattern
     from React Flow's background dots; mask it out. */
  .react-flow__background { display: none !important; }
  /* Free-tier credit line. Sits at the absolute bottom-right of the
     page, BELOW the title block so it's the last thing on the sheet
     and reads as a credit rather than diagram content. Muted grey,
     small enough to ignore but legible at print. */
  .sp-credit {
    position: absolute;
    right: 8px;
    bottom: 4px;
    font: 8px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
    color: #94a3b8;
    letter-spacing: 0.2px;
    pointer-events: none;
    z-index: 100;
  }
</style>
</head>
<body>
<div class="frame">${svgMarkup}${footer}</div>
</body>
</html>`;
}

/** Drawing number + rev → filename. Falls back to address-derived
 *  name, then plain "SLD". The PHP endpoint sanitises further. */
function deriveFilename(topology: Topology): string {
  const t = topology.project;
  const drawing = t?.drawingNumber?.trim();
  const rev = t?.revision?.trim();
  const addr = t?.address?.trim();
  // Take just the first comma-segment of the address (typically the
  // street) so we don't end up with "12 Foo St, Sydney NSW 2000.pdf".
  const addrShort = addr ? addr.split(',')[0]?.trim() : '';
  const stem =
    drawing
      ? (rev ? `${drawing}-rev${rev}` : drawing)
      : (addrShort || 'SLD');
  return `${stem.replace(/\s+/g, '_')}.pdf`;
}

/** Browser-native blob download — creates an invisible <a> with
 *  href=blob URL and clicks it. */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Defer revoke so the click has time to register
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}
